import type { StreamingChatClient, ChatStatus } from './types'

const responses = [
  `Great question! Here's a breakdown of the key concepts:

## Overview

When working with **streaming data**, there are several important considerations:

1. **Latency** - How quickly data arrives
2. **Throughput** - Volume of data per second
3. **Reliability** - Ensuring no data is lost

### Code Example

\`\`\`typescript
const stream = new ReadableStream({
  start(controller) {
    controller.enqueue('Hello');
    controller.close();
  }
});
\`\`\`

This pattern is commonly used in modern web applications.`,

  `I'd be happy to help with that! Let me walk you through the process.

## Step-by-Step Guide

First, let's understand the **core concepts**:

- **Server-Sent Events (SSE)** - One-way server-to-client
- **WebSockets** - Full duplex communication
- **HTTP Streaming** - Chunked transfer encoding

### Implementation Notes

> The choice between these depends on your specific use case and requirements.

Here's a simple comparison:

| Method | Direction | Complexity |
|--------|-----------|------------|
| SSE | Server → Client | Low |
| WebSocket | Bidirectional | Medium |
| HTTP | Request/Response | Low |

Let me know if you'd like more details on any of these!`,

  `Interesting! Here's what I think about that:

## Analysis

The approach you're describing has several **advantages**:

1. Simplicity in implementation
2. Good browser support
3. Automatic reconnection handling

### Example Usage

\`\`\`javascript
const eventSource = new EventSource('/api/stream');

eventSource.onmessage = (event) => {
  console.log('Received:', event.data);
};
\`\`\`

### Considerations

- Make sure to handle *connection errors* gracefully
- Consider implementing **exponential backoff** for reconnects
- Always clean up listeners when done

Would you like me to elaborate on any of these points?`,
]

function chunkText(text: string, minWords = 3, maxWords = 5): string[] {
  const words = text.split(' ')
  const chunks: string[] = []
  let i = 0

  while (i < words.length) {
    const chunkSize = Math.floor(Math.random() * (maxWords - minWords + 1)) + minWords
    const chunk = words.slice(i, i + chunkSize).join(' ')
    chunks.push(chunk)
    i += chunkSize
  }

  return chunks
}

export class MockStreamingChatClient implements StreamingChatClient {
  private status: ChatStatus = 'idle'
  private messageHandler: ((data: unknown) => void) | null = null
  private errorHandler: ((err: unknown) => void) | null = null
  private statusHandler: ((status: ChatStatus) => void) | null = null
  private abortController: AbortController | null = null

  async connect(): Promise<void> {
    this.setStatus('connecting')
    // Simulate connection delay
    await new Promise(resolve => setTimeout(resolve, 100))
    this.setStatus('connected')
  }

  disconnect(): void {
    this.abortController?.abort()
    this.abortController = null
    this.setStatus('disconnected')
  }

  send(_message: string | object): void {
    if (this.status !== 'connected') {
      this.errorHandler?.(new Error('Not connected'))
      return
    }

    this.abortController = new AbortController()
    const signal = this.abortController.signal

    const response = responses[Math.floor(Math.random() * responses.length)]!
    const chunks = chunkText(response)

    this.streamResponse(chunks, signal)
  }

  private async streamResponse(chunks: string[], signal: AbortSignal): Promise<void> {
    // Simulate status updates
    const statuses = ['Thinking...', 'Searching knowledge base...', 'Generating response...']
    
    for (const status of statuses) {
      if (signal.aborted) return
      this.messageHandler?.({ type: 'status', message: status })
      await new Promise(resolve => setTimeout(resolve, 400 + Math.random() * 300))
    }

    // Stream chunks
    for (let i = 0; i < chunks.length; i++) {
      if (signal.aborted) return

      await new Promise(resolve => setTimeout(resolve, 150 + Math.random() * 200))

      if (signal.aborted) return

      this.messageHandler?.({
        type: 'chunk',
        content: chunks[i] + (i < chunks.length - 1 ? ' ' : ''),
        done: false,
      })
    }

    if (!signal.aborted) {
      this.messageHandler?.({ type: 'done', done: true, status: 'complete' })
    }
  }

  onMessage(handler: (data: unknown) => void): void {
    this.messageHandler = handler
  }

  onError(handler: (err: unknown) => void): void {
    this.errorHandler = handler
  }

  onStatusChange(handler: (status: ChatStatus) => void): void {
    this.statusHandler = handler
  }

  private setStatus(status: ChatStatus): void {
    this.status = status
    this.statusHandler?.(status)
  }
}
